/**
 * Artikel (Mini-Version bis Etappe 4, mit Etiketten) und Personal & Rechte.
 */
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, FormGrid, PageHeader, Select } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { RECHTE_JE_ROLLE, type Recht } from "@shared/schemas/basis";

const RECHTE_ZEILEN: [Recht, string, string][] = [
  ["kassieren", "Kassieren", "Artikel scannen und Verkauf abschließen"],
  ["rabatt", "Rabatt geben", "Aushilfe nur mit PIN-Freigabe"],
  ["rabatt_ohne_freigabe", "Rabatt ohne Freigabe", "Bis zur Grenze aus den Einstellungen"],
  ["rueckgabe", "Rückgabe annehmen", "Geld zurück an Kundschaft"],
  ["auszahlung", "Auszahlung an Mieter", "Bar oder Überweisung (Etappe 6)"],
  ["vertraege", "Mietverträge", "Anlegen, verlängern, kündigen"],
  ["preise", "Preise ändern", "Regalklassen und Provision"],
  ["einstellungen", "Einstellungen", "Laden, Kasse, Regeln"],
  ["personal", "Personal", "Personen und PINs"],
  ["export", "Export", "Daten exportieren (Etappe 7)"],
];
import { formatEuro, parseEuro } from "@shared/geld";
import { formatDatum } from "@shared/zeit";
import { etikettenOeffnen, useArtikel, useArtikelAnlegen, type ArtikelZeile, useEinladungSenden, usePersonAendern, usePersonAnlegen, usePersonal, usePinSetzen } from "./stammdaten-api";
import { useMieterListe } from "./mieter-api";
import { useRegale } from "./regale-api";
import { useRechte, useRechteSpeichern, useSchwund, useSchwundMelden } from "./alltag-api";
import { useKommissionAnnehmen } from "./wachstum-api";
import { useLaden } from "./api";
import { formatDatumZeit } from "@shared/zeit";

function fehlerText(err: unknown, standard: string) {
  return err instanceof ApiFehler ? err.message : standard;
}

// --- Artikel -----------------------------------------------------------------------------------

export function Artikel() {
  const mieter = useMieterListe();
  const regale = useRegale();
  const [params] = useSearchParams();
  const [mieterId, setMieterId] = useState<string>(params.get("mieter") ?? "");
  const [status, setStatus] = useState<ArtikelZeile["status"] | "">("");
  const [suche, setSuche] = useState("");
  const artikel = useArtikel({ mieterId: mieterId || undefined, status: status || undefined, q: suche || undefined });
  const anlegen = useArtikelAnlegen();
  const schwund = useSchwundMelden();
  const [offen, setOffen] = useState(false);
  const [form, setForm] = useState({ mieterId: "", regalId: "", bezeichnung: "", preis: "", anzahl: "1" });
  const [fehler, setFehler] = useState<string | null>(null);
  const laden = useLaden();
  const kommission = useKommissionAnnehmen();
  const [kOffen, setKOffen] = useState(false);
  const [kForm, setKForm] = useState({ mieterId: "", bezeichnung: "", preis: "", anzahl: "1" });
  const [kFehler, setKFehler] = useState<string | null>(null);
  async function kommissionAbsenden(e: FormEvent) {
    e.preventDefault();
    setKFehler(null);
    const preisCent = parseEuro(kForm.preis);
    if (!kForm.mieterId) return setKFehler("Bitte den Mieter wählen.");
    if (!kForm.bezeichnung.trim()) return setKFehler("Bitte eine Bezeichnung eingeben.");
    if (!preisCent || preisCent <= 0) return setKFehler("Bitte einen Preis über 0,00 € eingeben.");
    try {
      const r = await kommission.mutateAsync({ mieterId: kForm.mieterId, bezeichnung: kForm.bezeichnung.trim(), preisCent, anzahl: Math.max(1, Math.min(50, Number(kForm.anzahl) || 1)) });
      setAuswahl(new Set(r.artikel.map((a) => a.id)));
      setMieterId(kForm.mieterId);
      setKForm({ ...kForm, bezeichnung: "", preis: "", anzahl: "1" });
      setKOffen(false);
    } catch (err) {
      setKFehler(fehlerText(err, "Annehmen hat nicht geklappt."));
    }
  }
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<"62x29" | "a4">("62x29");
  const [startfeld, setStartfeld] = useState("1");
  const [druckFehler, setDruckFehler] = useState<string | null>(null);
  const [druckt, setDruckt] = useState(false);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const preisCent = parseEuro(form.preis);
    if (!form.mieterId || !form.regalId) return setFehler("Bitte Mieter und Regal wählen.");
    if (!form.bezeichnung.trim()) return setFehler("Bitte eine Bezeichnung eingeben.");
    if (!preisCent || preisCent <= 0) return setFehler("Bitte einen Preis über 0,00 € eingeben.");
    try {
      const r = await anlegen.mutateAsync({ mieterId: form.mieterId, regalId: form.regalId, bezeichnung: form.bezeichnung.trim(), preisCent, anzahl: Math.max(1, Math.min(50, Number(form.anzahl) || 1)) });
      setAuswahl(new Set(r.artikel.map((a) => a.id)));
      setMieterId(form.mieterId);
      setForm({ ...form, bezeichnung: "", preis: "", anzahl: "1" });
      setOffen(false);
    } catch (err) {
      setFehler(fehlerText(err, "Anlegen hat nicht geklappt."));
    }
  }

  async function drucken() {
    setDruckFehler(null);
    setDruckt(true);
    try {
      await etikettenOeffnen([...auswahl], format, Number(startfeld) || 1);
    } catch (err) {
      setDruckFehler(err instanceof Error ? err.message : "Drucken hat nicht geklappt.");
    } finally {
      setDruckt(false);
    }
  }

  const alleIds = artikel.data?.artikel.map((a) => a.id) ?? [];
  return (
    <>
      <PageHeader kicker="Mieter" title="Artikel und Etiketten" sub="Die Mieter erfassen im Portal selbst; hier kann das Personal nachsehen, nacherfassen und Etiketten drucken" actions={<>{laden.data?.einstellungen.kommission.aktiv ? <Button variant="secondary" icon="package" onClick={() => setKOffen(true)}>Kommission annehmen</Button> : null}<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Artikel anlegen</Button></>} />
      <div className="sc-toolbar">
        <Select label="Mieter" value={mieterId} onChange={(e) => { setMieterId(e.target.value); setAuswahl(new Set()); }}>
          <option value="">Alle Mieter</option>
          {mieter.data?.mieter.map((m) => <option key={m.id} value={m.id}>{m.mieternummer} · {m.name}</option>)}
        </Select>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="">Alle</option>
          <option value="im_regal">Im Regal</option>
          <option value="verkauft">Verkauft</option>
          <option value="rueckgabe_kiste">Rückgabe-Kiste</option>
          <option value="abgeholt">Abgeholt</option>
          <option value="entfernt">Entfernt</option>
        </Select>
        <TextField label="Suche" icon="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Bezeichnung oder Code" />
        <Select label="Etikettenformat" value={format} onChange={(e) => setFormat(e.target.value as "62x29" | "a4")}>
          <option value="62x29">Etikettendrucker 62 × 29 mm</option>
          <option value="a4">A4-Bogen 4 × 11</option>
        </Select>
        {format === "a4" ? <TextField label="Startfeld" value={startfeld} onChange={(e) => setStartfeld(e.target.value)} inputMode="numeric" hint="1–44, bei angefangenem Bogen" /> : null}
        <div style={{ alignSelf: "flex-end" }}>
          <Button variant="secondary" icon="printer" disabled={auswahl.size === 0} loading={druckt} onClick={drucken}>Etiketten drucken ({auswahl.size})</Button>
        </div>
      </div>
      {druckFehler ? <Notice tone="error">{druckFehler}</Notice> : null}
      <Seitenzustand query={artikel} istLeer={(d) => d.artikel.length === 0} leer={<EmptyState illustration="empty-shelf" title="Noch keine Artikel" action={<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Ersten Artikel anlegen</Button>}>Jeder Artikel bekommt einen Code Mieter-Regal-Laufnummer und ein Etikett mit QR-Code.</EmptyState>}>
        {(d) => (
          <Card flush>
            <table className="rg-table">
              <thead><tr><th><input type="checkbox" aria-label="Alle auswählen" checked={auswahl.size > 0 && alleIds.every((id) => auswahl.has(id))} onChange={(e) => setAuswahl(e.target.checked ? new Set(alleIds) : new Set())} /></th><th>Foto</th><th>Code</th><th>Bezeichnung</th><th>Mieter</th><th>Regal</th><th className="is-num">Preis</th><th>Status</th><th>Erfasst</th><th><span className="rg-sr">Schwund</span></th></tr></thead>
              <tbody>
                {d.artikel.map((a) => (
                  <tr key={a.id}>
                    <td><input type="checkbox" aria-label={`${a.code} auswählen`} checked={auswahl.has(a.id)} onChange={(e) => { const n = new Set(auswahl); if (e.target.checked) n.add(a.id); else n.delete(a.id); setAuswahl(n); }} /></td>
                    <td>{a.fotoKey ? <img className="rg-thumb" src={`/api/laden/dateien/${a.fotoKey}`} alt="" loading="lazy" style={{ width: 40, height: 40 }} /> : <span className="sc-muted">–</span>}</td>
                    <td className="app-code">{a.code}</td><td>{a.bezeichnung}</td><td><Link to={`/laden/mieter/${a.mieterId}`}>{a.mieterName}</Link></td><td className="sc-num">{a.regalNummer}</td><td className="is-num">{formatEuro(a.preisCent)}</td>
                    <td><StatusBadge status={a.status === "verkauft" ? "sold" : a.status === "im_warenkorb" ? "wait" : a.status === "rueckgabe_kiste" ? "returnbox" : a.status === "im_regal" ? "shelf" : "neutral"}>{a.status === "im_warenkorb" ? "Im Warenkorb" : a.status === "abgeholt" ? "Abgeholt" : a.status === "entfernt" ? "Entfernt" : a.status === "schwund" ? "Schwund" : undefined}</StatusBadge></td>
                    <td className="sc-num">{formatDatum(a.erstelltAt)}</td>
                    <td>{a.status === "im_regal" || a.status === "rueckgabe_kiste" ? <Button variant="quiet" icon="triangle-alert" aria-label={`${a.code} als Schwund melden`} onClick={() => { const grund = prompt(`${a.bezeichnung} als Schwund melden – Grund (optional):`); if (grund !== null) schwund.mutate({ artikelId: a.id, grund }); }}>Schwund</Button> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </Seitenzustand>
      <SchwundKarte />
      <Dialog open={offen} title="Artikel anlegen" onClose={() => setOffen(false)} actions={<><Button variant="quiet" onClick={() => setOffen(false)}>Abbrechen</Button><Button variant="primary" type="submit" form="artikel-form" loading={anlegen.isPending}>Anlegen</Button></>}>
        <form id="artikel-form" className="app-form" onSubmit={absenden} noValidate>
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}
          <FormGrid>
            <Select label="Mieter" value={form.mieterId} onChange={(e) => setForm({ ...form, mieterId: e.target.value })} autoFocus>
              <option value="">Bitte wählen</option>
              {mieter.data?.mieter.map((m) => <option key={m.id} value={m.id}>{m.mieternummer} · {m.name}</option>)}
            </Select>
            <Select label="Regal" value={form.regalId} onChange={(e) => setForm({ ...form, regalId: e.target.value })}>
              <option value="">Bitte wählen</option>
              {regale.data?.regale.map((r) => <option key={r.id} value={r.id}>Regal {r.nummer}{r.lage ? ` · ${r.lage}` : ""}</option>)}
            </Select>
          </FormGrid>
          <TextField label="Bezeichnung" value={form.bezeichnung} onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })} placeholder="z. B. Blaue Vase, Glas" />
          <FormGrid>
            <TextField label="Preis" value={form.preis} onChange={(e) => setForm({ ...form, preis: e.target.value })} inputMode="decimal" suffix="€" placeholder="0,00" />
            <TextField label="Anzahl gleicher Artikel" value={form.anzahl} onChange={(e) => setForm({ ...form, anzahl: e.target.value })} inputMode="numeric" hint="Jeder bekommt eine eigene Laufnummer" />
          </FormGrid>
        </form>
      </Dialog>
      <Dialog open={kOffen} title="Kommission annehmen" onClose={() => setKOffen(false)} actions={<><Button variant="quiet" onClick={() => setKOffen(false)}>Abbrechen</Button><Button variant="primary" type="submit" form="kommission-form" loading={kommission.isPending}>Annehmen</Button></>}>
        <form id="kommission-form" className="app-form" onSubmit={(e) => void kommissionAbsenden(e)} noValidate>
          {kFehler ? <Notice tone="error">{kFehler}</Notice> : null}
          <p className="sc-muted sc-small">Ware ohne Regal: Code Mieter-000-Laufnummer, Provision {laden.data?.einstellungen.kommission.provisionProzent ?? 0} %, keine Regalmiete. Der Mieter braucht keinen Vertrag, nur ein Mieterkonto.</p>
          <Select label="Mieter (Kommissionsgeber)" value={kForm.mieterId} onChange={(e) => setKForm({ ...kForm, mieterId: e.target.value })} autoFocus>
            <option value="">Bitte wählen</option>
            {mieter.data?.mieter.map((m) => <option key={m.id} value={m.id}>{m.mieternummer} · {m.name}</option>)}
          </Select>
          <TextField label="Bezeichnung" value={kForm.bezeichnung} onChange={(e) => setKForm({ ...kForm, bezeichnung: e.target.value })} placeholder="z. B. Vintage-Sessel" />
          <FormGrid>
            <TextField label="Preis" value={kForm.preis} onChange={(e) => setKForm({ ...kForm, preis: e.target.value })} inputMode="decimal" suffix="€" placeholder="0,00" />
            <TextField label="Anzahl" value={kForm.anzahl} onChange={(e) => setKForm({ ...kForm, anzahl: e.target.value })} inputMode="numeric" />
          </FormGrid>
        </form>
      </Dialog>
    </>
  );
}

// --- Personal ----------------------------------------------------------------------------------


export function Personal({ eingebettet }: { eingebettet?: boolean } = {}) {
  const personal = usePersonal();
  const anlegen = usePersonAnlegen();
  const pinSetzen = usePinSetzen();
  const aendern = usePersonAendern();
  const einladen = useEinladungSenden();
  const [offen, setOffen] = useState(false);
  const [pinFuer, setPinFuer] = useState<{ id: string; name: string } | null>(null);
  const [form, setForm] = useState({ name: "", email: "", rolle: "mitarbeiter" as "inhaber" | "mitarbeiter" | "aushilfe" });
  const [rechteFuer, setRechteFuer] = useState<{ id: string; name: string; rolle: "mitarbeiter" | "aushilfe" } | null>(null);
  const [pin, setPin] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  async function personAbsenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    try {
      await anlegen.mutateAsync({ name: form.name.trim(), email: form.email.trim(), rolle: form.rolle });
      setForm({ name: "", email: "", rolle: "mitarbeiter" });
      setOffen(false);
    } catch (err) {
      setFehler(fehlerText(err, "Hinzufügen hat nicht geklappt."));
    }
  }
  async function pinAbsenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!/^\d{4,6}$/.test(pin)) return setFehler("PIN mit 4 bis 6 Ziffern.");
    try {
      await pinSetzen.mutateAsync({ benutzerId: pinFuer!.id, pin });
      setErfolg(`PIN für ${pinFuer!.name} gesetzt.`);
      setPin("");
      setPinFuer(null);
    } catch (err) {
      setFehler(fehlerText(err, "PIN konnte nicht gesetzt werden."));
    }
  }

  return (
    <>
      {eingebettet ? (
        <div className="sc-spread" style={{ marginBottom: "var(--space-3)" }}><h2>Personal</h2><Button variant="secondary" icon="plus" onClick={() => setOffen(true)}>Person hinzufügen</Button></div>
      ) : (
        <PageHeader kicker="Einstellungen" title="Personal und Rechte" sub="Anmeldung an der Kasse mit PIN, in der Verwaltung mit E-Mail-Link" actions={<Button variant="secondary" icon="plus" onClick={() => setOffen(true)}>Person hinzufügen</Button>} />
      )}
      {erfolg ? <Notice tone="success">{erfolg}</Notice> : null}
      <Seitenzustand query={personal}>
        {(d) => (
          <Card flush>
            <table className="rg-table">
              <thead><tr><th>Name</th><th>Rolle</th><th>Kassen-PIN</th><th>Zwei-Faktor</th><th>Zuletzt angemeldet</th><th><span className="rg-sr">Aktionen</span></th></tr></thead>
              <tbody>
                {d.personen.map((p) => (
                  <tr key={p.id} style={p.aktiv ? undefined : { opacity: 0.6 }}>
                    <td><span className="sc-strong">{p.name || p.email}</span><div className="sc-muted sc-small">{p.email}{p.aktiv ? "" : " · deaktiviert"}</div></td>
                    <td>
                      <select className="sc-select" aria-label={`Rolle ${p.name || p.email}`} value={p.rolle} onChange={(e) => aendern.mutate({ benutzerId: p.id, rolle: e.target.value as typeof p.rolle, aktiv: p.aktiv }, { onError: (err) => setFehler(fehlerText(err, "Rolle konnte nicht geändert werden.")) })}>
                        <option value="inhaber">Inhaber</option>
                        <option value="mitarbeiter">Mitarbeiter</option>
                        <option value="aushilfe">Aushilfe</option>
                      </select>
                    </td>
                    <td>{p.pinGesetzt ? <StatusBadge status="paid">Gesetzt</StatusBadge> : <StatusBadge status="attention">Nicht gesetzt</StatusBadge>}</td>
                    <td>{p.rolle === "aushilfe" ? <StatusBadge status="neutral">Nicht nötig</StatusBadge> : p.totp ? <StatusBadge status="paid">Aktiv</StatusBadge> : <StatusBadge status="neutral">ab Etappe 7</StatusBadge>}</td>
                    <td className="sc-muted">{p.letzterLoginAt ? formatDatumZeit(p.letzterLoginAt) : "noch nie"}</td>
                    <td>
                      <span className="sc-toolbar">
                        <Button variant="quiet" icon="lock" onClick={() => { setPinFuer({ id: p.id, name: p.name || p.email }); setFehler(null); }}>{p.pinGesetzt ? "PIN ändern" : "PIN setzen"}</Button>
                        {p.rolle !== "inhaber" ? <Button variant="quiet" icon="shield-check" onClick={() => setRechteFuer({ id: p.id, name: p.name || p.email, rolle: p.rolle as "mitarbeiter" | "aushilfe" })}>Rechte</Button> : null}
                        {p.rolle !== "aushilfe" ? <Button variant="quiet" icon="mail" loading={einladen.isPending} onClick={() => einladen.mutate(p.id, { onSuccess: () => setErfolg(`Anmeldelink an ${p.email} geschickt.`), onError: (err) => setFehler(fehlerText(err, "Senden hat nicht geklappt.")) })}>Anmeldelink</Button> : null}
                        {p.aktiv ? <Button variant="quiet" icon="x" onClick={() => confirm(`${p.name || p.email} deaktivieren? Die Person kann sich dann nicht mehr anmelden.`) && aendern.mutate({ benutzerId: p.id, rolle: p.rolle, aktiv: false }, { onError: (err) => setFehler(fehlerText(err, "Das hat nicht geklappt.")) })}>Deaktivieren</Button> : <Button variant="quiet" icon="check" onClick={() => aendern.mutate({ benutzerId: p.id, rolle: p.rolle, aktiv: true })}>Aktivieren</Button>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </Seitenzustand>
      {!eingebettet ? (
        <Card title="Rechte je Rolle" flush>
          <div className="app-tabellen-scroll">
            <table className="rg-table">
              <thead><tr><th>Recht</th><th>Inhaber</th><th>Mitarbeiter</th><th>Aushilfe</th></tr></thead>
              <tbody>
                {RECHTE_ZEILEN.map(([recht, text, sub]) => (
                  <tr key={recht}>
                    <td><span className="sc-strong">{text}</span><div className="sc-muted sc-small">{sub}</div></td>
                    {(["inhaber", "mitarbeiter", "aushilfe"] as const).map((r) => <td key={r}>{RECHTE_JE_ROLLE[r].includes(recht) ? <StatusBadge status="paid">Ja</StatusBadge> : <span className="sc-muted">–</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sc-muted sc-small" style={{ padding: "var(--space-3) var(--space-4)" }}>Standard je Rolle. Einzelne Rechte lassen sich je Person über „Rechte“ in der Tabelle an- oder abschalten. Rabatt-Freigabe und Kassensperre unter Kasse & SumUp.</p>
        </Card>
      ) : null}
      <Dialog open={offen} title="Person hinzufügen" onClose={() => setOffen(false)} size="sm" actions={<><Button variant="quiet" onClick={() => setOffen(false)}>Abbrechen</Button><Button variant="primary" type="submit" form="person-form" loading={anlegen.isPending}>Hinzufügen</Button></>}>
        <form id="person-form" className="app-form" onSubmit={personAbsenden} noValidate>
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          <TextField label="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} hint="Für die Anmeldung in der Verwaltung" />
          <Select label="Rolle" value={form.rolle} onChange={(e) => setForm({ ...form, rolle: e.target.value as typeof form.rolle })}>
            <option value="mitarbeiter">Mitarbeiter</option>
            <option value="aushilfe">Aushilfe</option>
            <option value="inhaber">Inhaber</option>
          </Select>
        </form>
      </Dialog>
      {rechteFuer ? <RechteDialog person={rechteFuer} onClose={() => setRechteFuer(null)} /> : null}
      <Dialog open={!!pinFuer} title={`Kassen-PIN für ${pinFuer?.name ?? ""}`} onClose={() => setPinFuer(null)} size="sm" actions={<><Button variant="quiet" onClick={() => setPinFuer(null)}>Abbrechen</Button><Button variant="primary" type="submit" form="pin-form" loading={pinSetzen.isPending}>PIN speichern</Button></>}>
        <form id="pin-form" className="app-form" onSubmit={pinAbsenden} noValidate>
          {fehler ? <Notice tone="error">{fehler}</Notice> : null}
          <TextField label="PIN (4 bis 6 Ziffern)" type="password" inputMode="numeric" autoComplete="one-time-code" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus hint="Die PIN gilt nur an der Kasse eines angemeldeten Geräts." />
        </form>
      </Dialog>
    </>
  );
}

/** E10.5: Rechte je Person – Standard der Rolle, je Recht an/aus (Überschreibung). */
function RechteDialog({ person, onClose }: { person: { id: string; name: string; rolle: "mitarbeiter" | "aushilfe" }; onClose: () => void }) {
  const q = useRechte(person.id);
  const speichern = useRechteSpeichern();
  const [ueber, setUeber] = useState<Record<string, boolean> | null>(null);
  const werte = ueber ?? q.data?.ueberschreibungen ?? {};
  const effektiv = (r: Recht) => (r in werte ? werte[r]! : RECHTE_JE_ROLLE[person.rolle].includes(r));
  return (
    <Dialog open title={`Rechte: ${person.name}`} size="md" onClose={onClose} actions={<><Button variant="quiet" onClick={onClose}>Abbrechen</Button><Button variant="primary" loading={speichern.isPending} onClick={() => speichern.mutate({ benutzerId: person.id, rechte: werte as Partial<Record<Recht, boolean>> }, { onSuccess: onClose })}>Speichern</Button></>}>
      <p className="sc-muted">Standard ist die Rolle „{person.rolle}“. Was du hier änderst, gilt nur für diese Person und steht im Protokoll.</p>
      <Seitenzustand query={q} lines={4}>
        {() => (
          <div className="sc-col" style={{ gap: 6 }}>
            {RECHTE_ZEILEN.map(([recht, text, sub]) => {
              const standard = RECHTE_JE_ROLLE[person.rolle].includes(recht);
              const an = effektiv(recht);
              return (
                <label key={recht} className="sc-check" style={{ alignItems: "flex-start" }}>
                  <input type="checkbox" checked={an} onChange={(e) => setUeber({ ...werte, [recht]: e.target.checked })} />
                  <span><span className="sc-strong">{text}</span>{an !== standard ? <StatusBadge status="attention">abweichend</StatusBadge> : null}<div className="sc-muted sc-small">{sub}</div></span>
                </label>
              );
            })}
            <Button variant="quiet" icon="undo-2" onClick={() => setUeber({})}>Auf Rolle zurücksetzen</Button>
          </div>
        )}
      </Seitenzustand>
    </Dialog>
  );
}

/** E10.6: Schwund – gemeldete Artikel und Häufung je Regal. */
function SchwundKarte() {
  const q = useSchwund();
  const [offen, setOffen] = useState(false);
  const d = q.data;
  if (!d || (!d.liste.length && !offen)) return null;
  return (
    <Card title={`Schwund · ${d.liste.length}`} actions={<Button variant="quiet" onClick={() => setOffen(!offen)}>{offen ? "Weniger" : "Details"}</Button>}>
      <p className="sc-muted sc-small">Als Schwund gemeldete Artikel (Mieter bekommt einen Hinweis). Häufung an einem Regal deutet auf eine schlechte Lage hin – Ladenplan prüfen.</p>
      {d.jeRegal.length ? <div className="sc-toolbar">{d.jeRegal.slice(0, 8).map((r) => <span key={r.regalNummer} className="sc-pill">Regal {r.regalNummer}: {r.n} · {formatEuro(r.wertCent)}</span>)}</div> : null}
      {offen ? (
        <table className="rg-table" style={{ marginTop: "var(--space-3)" }}>
          <thead><tr><th>Datum</th><th>Artikel</th><th>Mieter</th><th>Regal</th><th className="is-num">Preis</th><th>Grund</th></tr></thead>
          <tbody>{d.liste.map((x) => <tr key={x.id}><td>{formatDatum(x.erstelltAt)}</td><td>{x.bezeichnung} <span className="app-code sc-small">{x.code}</span></td><td>{x.mieterName}</td><td className="sc-num">{x.regalNummer}</td><td className="is-num">{formatEuro(x.preisCent)}</td><td className="sc-muted">{x.grund || "–"}</td></tr>)}</tbody>
        </table>
      ) : null}
    </Card>
  );
}
